import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL_PUBLIC } from "const/constants";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getClientDetailsFormConfig,
  parseClientDetailsFormConfig,
} from "utils/clientDetailsFormConfig";
import ClientDetailsFormFields from "views/admin/Settings/ClientDetailsForm/ClientDetailsFormFields";

const buildInitialState = (fields, prefill = {}) =>
  fields.reduce((acc, field) => {
    if (!field?.id) return acc;
    const isGroup = field.type === "checkbox-group";
    const provided = prefill?.[field.id];
    if (provided === undefined || provided === null || provided === "") {
      acc[field.id] = isGroup ? [] : "";
      return acc;
    }
    if (isGroup) {
      acc[field.id] = Array.isArray(provided) ? provided : [provided];
    } else {
      acc[field.id] = String(provided);
    }
    return acc;
  }, {});

export default function ClientDetailsPublicForm() {
  const [submitted, setSubmitted] = useState(false);

  const { slug } = useParams();

  // A referral partner's link opens on the record they already started, so the client reviews and
  // completes their details instead of typing them a second time.
  const {
    data: formInfo,
    isLoading: isLoadingInfo,
    isError: isLinkUnusable,
  } = useQuery({
    queryKey: ["public-client-form", slug],
    enabled: Boolean(slug),
    retry: false,
    queryFn: async () => {
      const res = await axios.get(`${API_URL_PUBLIC}/public/client-form/${slug}`, {
        headers: { Authorization: null },
      });
      return res?.data?.data ?? null;
    },
  });

  // The organization owns the field list, so it comes down with the link. The local copy is only
  // a fallback for a link opened before a config was ever saved.
  const fieldConfig = useMemo(
    () =>
      formInfo?.clientDetailsFieldsJson
        ? parseClientDetailsFormConfig(formInfo.clientDetailsFieldsJson)
        : getClientDetailsFormConfig(),
    [formInfo]
  );
  const enabledFields = useMemo(() => fieldConfig.filter((field) => field.enabled), [fieldConfig]);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (submitted) return;
    setForm(buildInitialState(enabledFields, formInfo?.prefill));
  }, [formInfo, enabledFields, submitted]);

  const { mutate, isLoading } = useMutation({
    mutationFn: (payload) =>
      axios.post(
        `${API_URL_PUBLIC}/public/client-form/${slug}`,
        { ...payload, formType: "client-details" },
        { headers: { Authorization: null } }
      ),
    onSuccess: () => {
      setForm(buildInitialState(enabledFields));
      setSubmitted(true);
    },
    onError: () => {
      toast.error("Could not submit your details. Please try again.");
    },
  });

  const handleChange = (id, value) =>
    setForm((prev) => ({ ...prev, [id]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!slug) {
      toast.error("Invalid form link");
      return;
    }
    mutate(form);
  };

  if (isLoading || isLoadingInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  // The link dies once it has been used or once the referrer hands the record to the office, so
  // the client gets told that rather than a form that fails on submit.
  if (isLinkUnusable && !submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-lg bg-app-surface rounded-xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-app-text mb-2">This link is no longer active</h2>
          <p className="text-app-text-secondary mb-0">
            It may have already been used, or your details may have been submitted for you. Please
            contact the person who referred you if you still need to send your details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-4xl bg-app-surface rounded-xl shadow-2xl overflow-hidden">
        <div className="relative bg-primary px-4 md:px-8 py-4 md:py-8">
          <div className="flex items-center gap-4">
            <img
              src={process.env.PUBLIC_URL + "/logo.png"}
              alt="Logo"
              className="w-24 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
                Client Details Form
              </h1>
              <p className="text-blue-100 text-sm font-medium">
                Please fill in the fields below. Required fields are marked with *.
              </p>
              {formInfo?.referredBy ? (
                <p className="text-blue-100 text-sm mt-1 mb-0">
                  Referred by {formInfo.referredBy}. Please check the details already filled in and
                  complete the rest.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-app-text mb-2">Details Submitted!</h2>
            <p className="text-app-text-secondary mb-2">
              Thank you. Your details have been received.
            </p>
            <p className="text-app-text-muted text-sm">You may close this tab now.</p>
          </div>
        ) : (
          <form className="p-6 md:p-8" onSubmit={handleSubmit}>
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-4 md:p-8 border border-gray-200/50 shadow-sm">
              <ClientDetailsFormFields
                fields={enabledFields}
                values={form}
                onChange={handleChange}
              />
            </div>
            <div className="pt-6 w-full">
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                className="w-full"
              >
                Submit
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
